import { useNavigate } from 'react-router'
import Alert from '@cloudscape-design/components/alert'
import BarChart from '@cloudscape-design/components/bar-chart'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Spinner from '@cloudscape-design/components/spinner'
import type { ContentStats, PostPerformanceStats, InsightBullet } from '@/lib/types'
import AIAnnotation from './AIAnnotation'

const ALL_PLATFORMS = ['Facebook', 'LinkedIn', 'Twitter', 'GBP']

interface Props {
  contentStats: ContentStats | null
  performanceStats: PostPerformanceStats | null
  loading: boolean
  insights?: InsightBullet[]
}

export default function SocialPerformanceWidget({ contentStats, performanceStats, loading, insights }: Props) {
  const navigate = useNavigate()

  if (loading) {
    return (
      <Box textAlign="center" padding="xl">
        <Spinner size="large" nativeAttributes={{ 'aria-label': 'Loading' }} />
      </Box>
    )
  }

  if (!contentStats && !performanceStats) {
    return (
      <Box textAlign="center" padding="l" color="text-body-secondary">
        No data available
      </Box>
    )
  }

  const connected = performanceStats?.platformsConnected ?? contentStats?.platformsConnected ?? []
  if (connected.length === 0) {
    return (
      <Alert
        statusIconAriaLabel="Info"
        header="Connect social platforms"
        action={<Button onClick={() => navigate('/settings/platforms')}>Connect Platforms</Button>}
      >
        Connect Facebook, LinkedIn, Twitter, or GBP to start publishing posts and tracking engagement here.
      </Alert>
    )
  }

  const unconnected = ALL_PLATFORMS.filter(p => !connected.includes(p))

  // Merge post counts and engagement onto a common platform list
  const postByPlatform = Object.fromEntries(
    (contentStats?.byPlatform ?? []).map(p => [p.platform, p.postCount])
  )
  const engByPlatform = Object.fromEntries(
    (performanceStats?.byPlatform ?? []).map(p => [p.platform, p])
  )
  const platforms = connected

  const maxY = Math.max(
    ...platforms.map(pl =>
      (postByPlatform[pl] ?? 0) +
      (engByPlatform[pl]?.likes ?? 0) +
      (engByPlatform[pl]?.comments ?? 0) +
      (engByPlatform[pl]?.shares ?? 0)
    ),
    1
  )

  return (
    <SpaceBetween size="s">
      <BarChart
        series={[
          {
            title: 'Posts Published',
            type: 'bar',
            data: platforms.map(pl => ({ x: pl, y: postByPlatform[pl] ?? 0 })),
          },
          {
            title: 'Likes',
            type: 'bar',
            data: platforms.map(pl => ({ x: pl, y: engByPlatform[pl]?.likes ?? 0 })),
          },
          {
            title: 'Comments',
            type: 'bar',
            data: platforms.map(pl => ({ x: pl, y: engByPlatform[pl]?.comments ?? 0 })),
          },
          {
            title: 'Shares',
            type: 'bar',
            data: platforms.map(pl => ({ x: pl, y: engByPlatform[pl]?.shares ?? 0 })),
          },
        ]}
        xDomain={platforms}
        yDomain={[0, maxY + Math.ceil(maxY * 0.1)]}
        stackedBars
        i18nStrings={{
          filterLabel: 'Filter series',
          filterPlaceholder: 'Filter series',
          filterSelectedAriaLabel: '(selected)',
          legendAriaLabel: 'Legend',
          chartAriaRoleDescription: 'bar chart',
          xTickFormatter: (v) => String(v),
          yTickFormatter: (v) => typeof v === 'number' ? v.toLocaleString() : String(v),
        }}
        ariaLabel="Social performance per platform"
        height={220}
      />
      {unconnected.length > 0 && (
        <Box variant="small" color="text-body-secondary">
          {unconnected.join(', ')} not connected.{' '}
          <Button variant="inline-link" onClick={() => navigate('/settings/platforms')}>Connect in Settings</Button>
        </Box>
      )}
      <AIAnnotation bullets={insights ?? []} />
    </SpaceBetween>
  )
}
